/*
 * Ecdysis — site behaviour.
 * Scroll, reveals, header, menu, cart drawer, quick add, search, product form.
 * Motion language: slow, viscous entrances (expo-out ~1s); fast, quiet exits.
 */
(() => {
  'use strict';

  const E = window.Ecdysis || {};
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  /* ------------------------------------------------------------ money */
  function money(cents) {
    const fmt = E.moneyFormat || '${{amount}}';
    const v = (Number(cents) || 0) / 100;
    const withDelim = (n, d, t, s) => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? s + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: withDelim(v, 2, ',', '.'),
      amount_no_decimals: withDelim(v, 0, ',', '.'),
      amount_with_comma_separator: withDelim(v, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelim(v, 0, '.', ',')
    }[k] || withDelim(v, 2, ',', '.')));
  }

  /* --------------------------------------------------------- scrolling */
  let lenis = null;
  function initScroll() {
    if (REDUCE || !window.Lenis) return;
    lenis = new window.Lenis({ duration: 1.15, easing: (t) => 1 - Math.pow(1 - t, 4), smoothWheel: true, wheelMultiplier: .95 });
    if (window.gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((time) => lenis.raf(time * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    $$('a[href^="#"]').forEach((a) => a.addEventListener('click', (e) => {
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) { e.preventDefault(); lenis.scrollTo(el, { offset: -80 }); }
    }));
  }
  const lockScroll = (on) => {
    document.documentElement.style.overflow = on ? 'hidden' : '';
    if (lenis) on ? lenis.stop() : lenis.start();
  };

  /* -------------------------------------------------------- split text */
  // Wrap every word in a mask so headlines rise line by line. Works through
  // <em> and other inline markup by walking text nodes only.
  function split(el) {
    if (el.__split) return;
    el.__split = true;
    if (el.querySelector('.ln')) {
      $$('.ln', el).forEach((l, i) => l.style.setProperty('--i', i));
      return;
    }
    const words = [];
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            const mask = document.createElement('span');
            mask.className = 'sw';
            const inner = document.createElement('span');
            inner.className = 'si';
            inner.textContent = part;
            mask.appendChild(inner);
            frag.appendChild(mask);
            words.push(mask);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    let line = -1, top = null;
    words.forEach((w) => {
      const t = w.offsetTop;
      if (top === null || Math.abs(t - top) > 4) { line++; top = t; }
      w.style.setProperty('--i', line);
    });
  }

  /* ----------------------------------------------------------- reveals */
  function initReveals(root = document) {
    const els = $$('[data-reveal], [data-split], [data-lineup]', root);
    $$('[data-split]', root).forEach(split);
    if (REDUCE || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    // A fully clipped element has no visible area, so IntersectionObserver
    // never sees it; clip reveals are observed through their parent instead.
    const pending = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        (pending.get(en.target) || [en.target]).forEach((t) => t.classList.add('is-in'));
        pending.delete(en.target);
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    els.forEach((el) => {
      if (el.hasAttribute('data-split-now')) {
        requestAnimationFrame(() => setTimeout(() => el.classList.add('is-in'), 120));
        return;
      }
      if (el.getAttribute('data-reveal') === 'clip' && el.parentElement) {
        const host = el.parentElement;
        if (!pending.has(host)) { pending.set(host, []); io.observe(host); }
        pending.get(host).push(el);
        return;
      }
      io.observe(el);
    });
  }

  /* ------------------------------------- lineup: swipe carousel (mobile) */
  function initLineup() {
    $$('[data-lineup]').forEach((row) => {
      const nav = row.parentElement.querySelector('[data-lineup-nav]');
      const items = $$('.lineup__item', row);
      if (!nav || items.length < 2) return;
      const idx = nav.querySelector('[data-lineup-index]');
      const prev = nav.querySelector('[data-lineup-prev]');
      const next = nav.querySelector('[data-lineup-next]');
      nav.style.setProperty('--count', items.length);
      row.setAttribute('data-carousel', '');
      let active = -1, ticking = false;
      const update = () => {
        ticking = false;
        const mid = row.scrollLeft + row.clientWidth / 2;
        let best = 0, dist = Infinity;
        items.forEach((it, i) => {
          const d = Math.abs(it.offsetLeft + it.offsetWidth / 2 - mid);
          if (d < dist) { dist = d; best = i; }
        });
        if (best === active) return;
        active = best;
        items.forEach((it, i) => it.classList.toggle('is-active', i === best));
        idx.textContent = String(best + 1).padStart(2, '0');
        nav.style.setProperty('--at', best);
        prev.disabled = best === 0;
        next.disabled = best === items.length - 1;
      };
      const go = (dir) => {
        const target = items[Math.max(0, Math.min(items.length - 1, active + dir))];
        row.scrollTo({ left: target.offsetLeft + target.offsetWidth / 2 - row.clientWidth / 2, behavior: REDUCE ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => go(-1));
      next.addEventListener('click', () => go(1));
      row.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', () => { active = -1; update(); });
      update();
    });
  }

  /* ---------------------------------------- manifesto: words light up */
  function initScrub() {
    $$('[data-scrub]').forEach((el) => {
      const words = [];
      const walk = (node) => Array.from(node.childNodes).forEach((c) => {
        if (c.nodeType === 3) {
          const frag = document.createDocumentFragment();
          c.textContent.split(/(\s+)/).forEach((p) => {
            if (!p) return;
            if (/^\s+$/.test(p)) return frag.appendChild(document.createTextNode(' '));
            const s = document.createElement('span');
            s.className = 'w';
            s.textContent = p;
            frag.appendChild(s);
            words.push(s);
          });
          c.replaceWith(frag);
        } else if (c.nodeType === 1) walk(c);
      });
      walk(el);
      if (REDUCE || !window.ScrollTrigger) { words.forEach((w) => (w.style.opacity = 1)); return; }
      let lit = -1;
      window.ScrollTrigger.create({
        trigger: el,
        start: 'top 78%',
        end: 'bottom 45%',
        onUpdate: (st) => {
          const n = Math.round(st.progress * words.length);
          if (n === lit) return;
          lit = n;
          words.forEach((w, i) => (w.style.opacity = i < n ? 1 : ''));
        }
      });
    });
  }

  /* ------------------------------------------------- photos in & out */
  // Photographs open from a framed window as they arrive, sit full while in
  // view, and close back down as they leave — all tied to scroll position,
  // so it runs both ways. The image inside drifts against the frame.
  function initMedia() {
    if (REDUCE || !window.gsap || !window.ScrollTrigger) return;
    const g = window.gsap;
    g.registerPlugin(window.ScrollTrigger);
    const mm = g.matchMedia();
    mm.add({ desk: '(min-width: 990px)', mob: '(max-width: 989px)' }, (ctx) => {
      const k = ctx.conditions.desk ? 1 : .55;
      $$('[data-media]').forEach((el) => {
        const img = $('img', el);
        const inset = (y, x) => `inset(${y * k}% ${x * k}% ${y * k}% ${x * k}% round ${2 * k}px)`;
        const tl = g.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: .6 }
        });
        tl.fromTo(el, { clipPath: inset(16, 9) }, { clipPath: inset(0, 0), duration: .38 })
          .to(el, { clipPath: inset(0, 0), duration: .3 })
          .to(el, { clipPath: inset(12, 6), duration: .32 });
        if (img) {
          g.fromTo(img, { scale: 1.22, yPercent: -5 }, {
            scale: 1.06, yPercent: 5, ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: .6 }
          });
        }
      });
      $$('[data-float]').forEach((el) => {
        g.fromTo(el, { yPercent: 8, rotate: -2 }, {
          yPercent: -8, rotate: 2, ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: .8 }
        });
      });
      if (ctx.conditions.desk) {
        const word = $('[data-footer-word]');
        if (word) {
          g.fromTo(word, { letterSpacing: '.24em' }, {
            letterSpacing: '.02em', ease: 'none',
            scrollTrigger: { trigger: word, start: 'top bottom', end: 'bottom bottom', scrub: true }
          });
        }
      }
    });
    window.addEventListener('load', () => window.ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => window.ScrollTrigger.refresh());
  }

  /* ------------------------------------- shirt lines, word by word */
  function initWords(root = document) {
    $$('[data-words]', root).forEach((el) => {
      if (el.__words) return;
      el.__words = true;
      const words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach((w, i) => {
        const s = document.createElement('span');
        s.className = 'w';
        s.style.setProperty('--wi', i);
        s.textContent = w;
        el.appendChild(s);
        el.appendChild(document.createTextNode(' '));
      });
    });
  }

  /* ------------------------------------------------------------ header */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const overlay = header.hasAttribute('data-overlay');
    let lastY = window.scrollY, ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (overlay) header.classList.toggle('is-solid', y > window.innerHeight * .7);
      const goingDown = y > lastY + 4, goingUp = y < lastY - 4;
      if (y > 480 && goingDown && !document.body.classList.contains('has-overlay')) header.classList.add('is-hidden');
      else if (goingUp || y < 480) header.classList.remove('is-hidden');
      if (goingDown || goingUp) lastY = y;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* ---------------------------------------------- dialogs (shared bits) */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function dialog(root, { onOpen, onClose } = {}) {
    let opener = null;
    const keydown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key !== 'Tab') return;
      const f = $$(FOCUSABLE, root).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    function open(trigger) {
      if (root.classList.contains('is-open')) return;
      opener = trigger || document.activeElement;
      root.classList.add('is-open');
      document.body.classList.add('has-overlay');
      lockScroll(true);
      document.addEventListener('keydown', keydown);
      if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'true');
      onOpen && onOpen();
    }
    function close() {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      document.body.classList.remove('has-overlay');
      lockScroll(false);
      document.removeEventListener('keydown', keydown);
      if (opener && opener.setAttribute) opener.setAttribute('aria-expanded', 'false');
      onClose && onClose();
      if (opener && opener.focus) opener.focus({ preventScroll: true });
    }
    return { open, close, get isOpen() { return root.classList.contains('is-open'); } };
  }

  /* -------------------------------------------------------------- menu */
  function initMenu() {
    const panel = $('[data-menu-panel]');
    if (!panel) return;
    const d = dialog(panel, { onOpen: () => setTimeout(() => $('[data-menu-close]', panel).focus(), 60) });
    $$('[data-menu-open]').forEach((b) => b.addEventListener('click', () => d.open(b)));
    $$('[data-menu-close]', panel).forEach((b) => b.addEventListener('click', () => d.close()));
    $$('a', panel).forEach((a) => a.addEventListener('click', () => d.close()));
  }

  /* -------------------------------------------------------------- cart */
  const Cart = {
    drawer: null, d: null,
    init() {
      const wrap = $('[data-cart-drawer]');
      if (!wrap) return;
      this.drawer = wrap;
      this.d = dialog(wrap, { onOpen: () => setTimeout(() => { const c = $('[data-cart-close].icon-btn', wrap); c && c.focus(); }, 80) });
      document.addEventListener('click', (e) => {
        const open = e.target.closest('[data-cart-open]');
        if (open && !document.body.classList.contains('template-cart')) { e.preventDefault(); this.d.open(open); }
        if (e.target.closest('[data-cart-close]')) this.d.close();
      });
      this.bindLines(document);
    },
    sectionIds() {
      const ids = ['cart-drawer'];
      if ($('[data-cart-page]')) ids.push($('[data-cart-page]').dataset.sectionId);
      return ids;
    },
    render(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (sections['cart-drawer']) {
        const doc = parse(sections['cart-drawer']);
        const fresh = $('[data-cart-root]', doc);
        const root = $('[data-cart-root]', this.drawer);
        if (fresh && root) root.innerHTML = fresh.innerHTML;
        this.bindLines(this.drawer);
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const doc = parse(sections[page.dataset.sectionId]);
        const fresh = $('[data-cart-page]', doc);
        if (fresh) page.innerHTML = fresh.innerHTML;
        this.bindLines(page);
      }
    },
    count(n) {
      $$('[data-cart-count]').forEach((el) => {
        el.dataset.count = n;
        el.textContent = n > 0 ? n : '';
        el.classList.remove('is-bump');
        void el.offsetWidth;
        if (n > 0) el.classList.add('is-bump');
      });
    },
    async add(id, quantity = 1, props) {
      const body = { items: [{ id: Number(id), quantity, properties: props }], sections: this.sectionIds().join(','), sections_url: window.location.pathname };
      const res = await fetch(E.routes.cartAdd + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add');
      this.render(data.sections);
      const cart = await (await fetch(E.routes.cart + '.js')).json();
      this.count(cart.item_count);
      return data;
    },
    async change(line, quantity, el) {
      el && el.classList.add('is-busy');
      try {
        const res = await fetch(E.routes.cartChange + '.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: this.sectionIds().join(','), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Could not update');
        this.render(data.sections);
        this.count(data.item_count);
      } catch (err) {
        el && el.classList.remove('is-busy');
        console.warn(err);
      }
    },
    bindLines(root) {
      $$('[data-line]', root).forEach((row) => {
        if (row.__bound) return;
        row.__bound = true;
        const line = row.dataset.line;
        const input = $('[data-qty-input]', row);
        $$('[data-qty-step]', row).forEach((b) => b.addEventListener('click', () => {
          const q = Math.max(0, Number(input.value) + Number(b.dataset.qtyStep));
          this.change(line, q, row);
        }));
        input && input.addEventListener('change', () => this.change(line, Math.max(0, Number(input.value) || 0), row));
        const rm = $('[data-line-remove]', row);
        rm && rm.addEventListener('click', () => this.change(line, 0, row));
      });
    }
  };

  /* --------------------------------------------------------- quick add */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const toggle = e.target.closest('[data-tray-toggle]');
      if (toggle) {
        const card = toggle.closest('[data-card]');
        const open = !card.classList.contains('is-tray-open');
        $$('[data-card].is-tray-open').forEach((c) => { c.classList.remove('is-tray-open'); const t = $('[data-tray-toggle]', c); t && t.setAttribute('aria-expanded', 'false'); });
        card.classList.toggle('is-tray-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        return;
      }
      const btn = e.target.closest('[data-quick-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.classList.add('is-busy');
      const label = btn.textContent;
      try {
        await Cart.add(btn.dataset.quickAdd, 1);
        btn.textContent = '✓';
        const card = btn.closest('[data-card]');
        card && card.classList.remove('is-tray-open');
        Cart.d && Cart.d.open(btn);
      } catch (err) {
        btn.textContent = '×';
        console.warn(err);
      } finally {
        setTimeout(() => { btn.classList.remove('is-busy'); btn.textContent = label; }, 1400);
      }
    });
  }

  /* ------------------------------------------------------------ search */
  function initSearch() {
    const wrap = $('[data-search-overlay]');
    if (!wrap) return;
    const input = $('[data-search-input]', wrap);
    const results = $('[data-search-results]', wrap);
    const d = dialog(wrap, { onOpen: () => setTimeout(() => input.focus(), 80) });
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', () => d.open(b)));
    $$('[data-search-close]', wrap).forEach((b) => b.addEventListener('click', () => d.close()));
    let timer, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        ctrl && ctrl.abort();
        ctrl = new AbortController();
        try {
          const url = `${E.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5&section_id=predictive-search`;
          const html = await (await fetch(url, { signal: ctrl.signal })).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('[data-predictive]', doc);
          results.innerHTML = fresh ? fresh.innerHTML : '';
          initReveals(results);
          window.Molt && window.Molt.bind(results);
        } catch (err) { if (err.name !== 'AbortError') console.warn(err); }
      }, 180);
    });
  }

  /* ---------------------------------------------------- product form */
  function initProduct() {
    $$('[data-product]').forEach((root) => {
      const dataEl = $('[data-product-json]', root);
      if (!dataEl) return;
      const product = JSON.parse(dataEl.textContent);
      const form = $('[data-product-form]', root);
      const idInput = $('input[name="id"]', form);
      const buy = $('[data-add]', root);
      const buyLabel = buy && $('.btn__label', buy);
      const priceEl = $('[data-price]', root);
      const err = $('[data-error]', root);
      const colorPos = Number(root.dataset.colorPos || 0);
      const gallery = $('[data-gallery]', root);
      const sticky = $('[data-sticky-buy]');

      const selected = () => $$('[data-option]', root).map((fs) => {
        const c = $('input:checked', fs);
        return c ? c.value : null;
      });
      const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

      function markAvailability(opts) {
        $$('[data-option]', root).forEach((fs, idx) => {
          $$('input', fs).forEach((inp) => {
            const test = opts.slice();
            test[idx] = inp.value;
            const v = findVariant(test);
            inp.classList.toggle('is-unavailable', !v || !v.available);
          });
          const label = $('[data-option-value]', fs);
          const c = $('input:checked', fs);
          if (label && c) label.textContent = c.value;
        });
      }

      // Variant images: the merchant's photos map one front shot per colour.
      // Keep the lead (back print) first, put the chosen colour's shots next.
      const lead = gallery ? $('[data-colors]', gallery) : null;
      function filterGallery(v, scroll) {
        if (!gallery || !colorPos || !v) return;
        const color = v.options[colorPos - 1];
        const own = $$('[data-colors]', gallery).filter((it) => it.dataset.colors && it.dataset.colors.split('|').includes(color));
        if (!own.length) return;
        let anchor = lead && !own.includes(lead) ? lead : null;
        own.forEach((it) => {
          if (anchor) anchor.after(it); else gallery.prepend(it);
          anchor = it;
          it.classList.add('is-in');
        });
        $$('[data-colors]', gallery).forEach((it, i) => it.classList.toggle('is-extra', i >= 7));
        if (scroll && window.innerWidth < 990) gallery.scrollTo({ left: own[0].offsetLeft - gallery.offsetLeft, behavior: REDUCE ? 'auto' : 'smooth' });
      }
      const more = $('[data-gallery-more]', root);
      more && more.addEventListener('click', () => {
        gallery.classList.add('is-expanded');
        more.remove();
        window.ScrollTrigger && window.ScrollTrigger.refresh();
      });

      function update(push = true) {
        const opts = selected();
        const v = findVariant(opts);
        markAvailability(opts);
        err && (err.textContent = '');
        if (!v) {
          if (buy) { buy.disabled = true; buyLabel.textContent = E.strings.unavailable; }
          return;
        }
        idInput.value = v.id;
        if (priceEl) {
          priceEl.innerHTML = v.compare_at_price > v.price
            ? `<s>${money(v.compare_at_price)}</s><span class="is-sale">${money(v.price)}</span>`
            : money(v.price);
        }
        if (buy) {
          buy.disabled = !v.available;
          buyLabel.textContent = v.available ? E.strings.addToCart : E.strings.soldOut;
        }
        if (sticky) {
          const sb = $('[data-sticky-add]', sticky);
          const sp = $('[data-sticky-price]', sticky);
          if (sb) sb.disabled = !v.available;
          if (sp) sp.textContent = money(v.price) + (v.title !== 'Default Title' ? ' · ' + v.title.replace(/Faded /g, '') : '');
        }
        filterGallery(v, push);
        if (push) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      $$('[data-option] input', root).forEach((inp) => inp.addEventListener('change', () => update(true)));
      update(false);

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!buy || buy.disabled) return;
        buy.setAttribute('aria-busy', 'true');
        const prev = buyLabel.textContent;
        try {
          await Cart.add(idInput.value, 1);
          buyLabel.textContent = E.strings.added;
          Cart.d && Cart.d.open(buy);
        } catch (ex) {
          err && (err.textContent = ex.message);
        } finally {
          buy.removeAttribute('aria-busy');
          setTimeout(() => (buyLabel.textContent = prev), 1600);
        }
      });

      if (sticky && buy) {
        // shown once the main buy button has scrolled up out of view
        let queued = false;
        const check = () => { queued = false; sticky.classList.toggle('is-visible', buy.getBoundingClientRect().bottom < 0); };
        window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(check); } }, { passive: true });
        check();
        const sb = $('[data-sticky-add]', sticky);
        sb && sb.addEventListener('click', () => form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })));
      }

      // mobile gallery counter
      const counter = $('[data-gallery-count]', root);
      if (gallery && counter) {
        gallery.addEventListener('scroll', () => {
          const items = $$('[data-colors]:not(.is-filtered-out)', gallery);
          const w = items[0] ? items[0].getBoundingClientRect().width : 1;
          const i = Math.min(items.length, Math.round(gallery.scrollLeft / w) + 1);
          counter.textContent = `${i} / ${items.length}`;
        }, { passive: true });
      }
    });
  }

  /* ------------------------------------------------- recommendations */
  function initRelated() {
    $$('[data-related]').forEach(async (el) => {
      if (el.children.length) return;
      try {
        const html = await (await fetch(el.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-related]', doc);
        if (!fresh || !fresh.innerHTML.trim()) return;
        el.innerHTML = fresh.innerHTML;
        initReveals(el);
        window.Molt && window.Molt.bind(el);
        window.ScrollTrigger && window.ScrollTrigger.refresh();
      } catch (err) { console.warn(err); }
    });
  }

  /* ------------------------------------------------------ collection */
  function initCollection() {
    $$('[data-sort]').forEach((sel) => sel.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }));
  }

  /* -------------------------------------------------------------- boot */
  function boot() {
    if (window.gsap && window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
    initScroll();
    initHeader();
    initMenu();
    Cart.init();
    initQuickAdd();
    initSearch();
    initProduct();
    initCollection();
    initRelated();
    initReveals();
    initLineup();
    initScrub();
    initWords();
    initMedia();
  }

  window.EcdysisCart = Cart;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
